import { all, call, put, select, takeEvery, takeLatest } from 'typed-redux-saga'
import { get } from 'lodash'
import moment from 'moment'
import { ActionTypes } from 'Src/constants/ActionType.constants'
import { RequestParams } from 'Models'
import { Action } from 'Types/action'
import { DATE_FORMAT, DatePeriod, PARAMS_FOR_USER_CHECK } from 'Src/constants/Global'
import { Roles, RolesHasStartPage } from 'Src/constants/Roles'
import { authSelector, AccessCheck, authApi, hasAddedProps, isChecked } from 'Pages/auth'
import { routerActions } from 'connected-react-router'
import { FrontUrls } from 'Src/constants/FrontUrls'
import {
	dashboardActions,
	GET_DASHBOARD_WIDGETS_DATA,
	UPDATE_MOTIVATION_WIDGET,
	getAvailableProductsApi,
	getDashBoardDataApi,
	getWidgetDataApi,
	dashboardSelectors,
	DashboardParams,
	DashboardParamsApi,
	DashboardData,
	WidgetData,
	WidgetNames,
	WidgetsWithFilters,
	WidgetsWithForecast,
	ProductPackagesType,
	updateMotivationWidgetApi,
} from 'Pages/dashboard'
import { actSlice } from 'Pages/acts'
import {
	modalsSlice,
	PartnerPaymentModal,
	errorHandler,
	SETTING_KEY_USER,
	userSettingSlice,
	userSettingSelectors,
	UserSettingNS,
	loadersSlice,
} from 'Modules'
import { MODAL_NAME } from 'Constants/ModalName'

export function* getDashBoardData() {
	try {
		const params = yield* select(dashboardSelectors.getDashboardDateInfoSelector)
		const partner_id = yield* select(
			userSettingSelectors.getSettings(SETTING_KEY_USER.DASHBOARD_PARTNER_ID)
		)
		const { period, date_at, date_to } = params
		const res = yield* call(getDashBoardDataApi, { period, from: date_at, to: date_to, partner_id })
		const authData = yield* select(authSelector.getAuthDataSelector)
		let resultData:
			| Partial<DashboardData>
			| Pick<DashboardData, 'manager' | 'partner' | 'widgets' | 'partner_id'>
		const accessCheck = new AccessCheck(authData?.roles)
		if (!accessCheck.hasRole([Roles.ADMIN, Roles.SUPER_ADMIN])) {
			const checkUser = yield* call(
				authApi.getCheckUser,
				authData.partner_id,
				PARAMS_FOR_USER_CHECK
			)
			resultData = { ...res, partner_id: checkUser }
		} else {
			resultData = res
		}
		yield* put(dashboardActions.setDashboardDataAction(resultData))
	} catch (error) {
		yield* call(errorHandler, error)
		if (error.status === 404) {
			yield* put(
				userSettingSlice.actions.updateSettings({
					data: null,
					key: SETTING_KEY_USER.DASHBOARD_PARTNER_ID,
				})
			)
			yield* put(userSettingSlice.actions.getSettings())
		}
	}
}

function* getWidgetData(widget: WidgetNames, dateInfo: DashboardParams, partner_id: number) {
	const { period, date_at, date_to, products_group, products_ids } = dateInfo
	const params: DashboardParamsApi = { partner_id }
	if (WidgetsWithFilters.includes(widget)) {
		params.period = period !== DatePeriod.CUSTOM ? period : undefined
		params.is_current_period = Boolean(
			period !== DatePeriod.CUSTOM &&
				WidgetsWithForecast.includes(widget) &&
				date_to === moment().format(DATE_FORMAT)
		)
		params.from = date_at
		params.to = date_to
		params.products_group = products_group !== ProductPackagesType.ALL ? products_group : undefined
		params.products_ids = products_ids
	}
	if (widget === WidgetNames.CAPABILITIES) {
		params.full = false
	}

	const res = yield* call(getWidgetDataApi, widget, params)
	yield* put(
		dashboardActions.setDashboardWidgetDataAction(
			widget,
			res.error ? {} : (res as WidgetData),
			dateInfo
		)
	)
}
export function* getWidgetsData(action: Action<GET_DASHBOARD_WIDGETS_DATA>) {
	try {
		const dateInfo = yield* select(dashboardSelectors.getDashboardDateInfoSelector)
		const partner_id = yield* select(
			userSettingSelectors.getSettings(SETTING_KEY_USER.DASHBOARD_PARTNER_ID)
		)
		yield* all(action.payload.map((widget) => call(getWidgetData, widget, dateInfo, partner_id)))
	} catch (error) {
		/* ошибка с перезакрепленным партнером, перехватывается в запросе dashboard, здесь блокируется,
		 чтобы не вызывать множественных сообщений пользователю и запросов к серверу */
		if (error.status === 404) {
			return
		}
		yield* call(errorHandler, error)
	} finally {
		yield* put(dashboardActions.setDashboardWidgetsIsLoading(false))
	}
}

export function* getWidgetCapabilitiesFullData() {
	try {
		const partner_id = yield* select(dashboardSelectors.getDashboardPartnerSelector)
		const res = yield* call(getWidgetDataApi, WidgetNames.CAPABILITIES, { full: true, partner_id })
		yield* put(dashboardActions.setDashboardWidgetCapabilitiesDataFullAction(res))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export function* setSettings(action: UserSettingNS.SetSettings) {
	try {
		const nextPartnerId = get(action.payload, 'data.dashboard_partner_id', null)
		const partner_id = yield* select(dashboardSelectors.getDashboardPartnerSelector)
		if (partner_id !== nextPartnerId)
			yield* put(dashboardActions.setDashboardPartnerIdAction(nextPartnerId))
		const authData = yield* select(authSelector.getAuthDataSelector)
		const accessCheck = new AccessCheck(authData.roles)
		if (!nextPartnerId && accessCheck.hasRole(RolesHasStartPage)) {
			yield* put(dashboardActions.showDashboardPartnerSelectAction())
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export function* showSigningActsSaga() {
	const dashboardPartnerId = yield* select(dashboardSelectors.getDashboardPartnerSelector)
	yield* put(
		actSlice.actions.setActsRequestParams(
			Object.assign(new RequestParams(), {
				status_slug: 'on_partner_signing',
				partner_id: dashboardPartnerId,
			})
		)
	)
	yield* put(routerActions.push(FrontUrls.new_acts))
}

export function* getDashboardAvailableProducts() {
	try {
		const params = yield* select(dashboardSelectors.getDashboardDateInfoSelector)
		const partner_id = yield* select(
			userSettingSelectors.getSettings(SETTING_KEY_USER.DASHBOARD_PARTNER_ID)
		)
		const { period, date_at, date_to, products_group } = params
		const res = yield* call(getAvailableProductsApi, {
			period: period !== DatePeriod.CUSTOM ? period : undefined,
			from: date_at,
			to: date_to,
			products_group: products_group !== ProductPackagesType.ALL ? products_group : undefined,
			partner_id,
		})
		yield* put(dashboardActions.setDashboardAvailableProducts(res.available_products))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export function* showDashboardPartnerPayment() {
	const auth = yield* select(authSelector.getAuth)
	const checkUser = yield* call(authApi.getCheckUser, auth?.partner_id, PARAMS_FOR_USER_CHECK)
	const accessCheck = new AccessCheck(auth?.roles)
	const isCheck = isChecked(checkUser)
	const showAddedProps = hasAddedProps(auth?.partner?.channels, checkUser, true)
	if (accessCheck.hasRole([Roles.RUKOVODITEL])) {
		yield* put(
			modalsSlice.actions.showModal({
				Component: PartnerPaymentModal,
				key: MODAL_NAME.PARTNER_PAYMENT,
				width: '720px',
				title: null,
				componentProps: { item: checkUser, isCheck, showAddedProps },
				closable: true,
				maskClosable: true,
				keyboard: false,
				hideOnlyByUser: true,
			})
		)
	}
}

export function* updateMotivationWidget(action: Action<UPDATE_MOTIVATION_WIDGET>) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const dateInfo = yield* select(dashboardSelectors.getDashboardDateInfoSelector)
		const partner_id = yield* select(
			userSettingSelectors.getSettings(SETTING_KEY_USER.DASHBOARD_PARTNER_ID)
		)
		const dashboardData = yield* select(dashboardSelectors.getDashboardDataSelector)
		yield* call(
			updateMotivationWidgetApi,
			partner_id ?? dashboardData?.partner_id?.id ?? '',
			action.payload.data
		)
		yield* call(getWidgetData, WidgetNames.REWARD, dateInfo, partner_id)
		action?.payload?.callback()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

export const dashboardSaga = function* sagas() {
	yield* takeLatest(ActionTypes.GET_DASHBOARD_DATA, getDashBoardData)
	yield* takeEvery(ActionTypes.GET_DASHBOARD_WIDGETS_DATA, getWidgetsData)
	yield* takeEvery(
		ActionTypes.GET_DASHBOARD_WIDGET_CAPABILITIES_DATA_FULL,
		getWidgetCapabilitiesFullData
	)
	yield* takeEvery(userSettingSlice.actions.setSettings, setSettings)
	yield* takeLatest(ActionTypes.DASHBOARD_SHOW_SIGNING_ACTS, showSigningActsSaga)
	yield* takeLatest(ActionTypes.GET_DASHBOARD_AVAILABLE_PRODUCTS, getDashboardAvailableProducts)
	yield* takeLatest(ActionTypes.SHOW_DASHBOARD_PARTNER_PAYMENT, showDashboardPartnerPayment)
	yield* takeLatest(ActionTypes.UPDATE_MOTIVATION_WIDGET, updateMotivationWidget)
}
